/**
 * REST 클라이언트 — §10 인증·§8.3 히스토리·§8.4 삭제.
 *
 * **계약 정본은 서버다.** 경로·요청 본문·응답 필드는 server/main.py 의
 * 라우트에 맞춘다. 목(src/mock/plugin.ts)이 같은 계약을 흉내 내는 쪽이지
 * 그 반대가 아니다 — 목을 정본으로 삼았던 탓에 웹 전체가 존재하지 않는
 * 엔드포인트를 향해 짜여 있었다.
 */

import { API_BASE, HISTORY_PAGE } from './config'
import { accountAuthHeaders, clearAccountToken } from './token'
import type { Envelope } from './events'

export class ApiError extends Error {
  readonly status: number
  readonly code: string

  constructor(status: number, code: string, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }
}

/** 서버 오류 본문은 FastAPI 관례대로 `{detail}` 이다(§19 — 형태 통일). */
function errorFrom(status: number, body: unknown): ApiError {
  const rec = (body ?? {}) as { detail?: unknown }
  const detail = rec.detail
  // detail 은 문자열(우리 코드) 또는 검증 오류 배열(FastAPI 기본)일 수 있다.
  const code = typeof detail === 'string' ? detail : 'unknown'
  return new ApiError(status, code, MESSAGES[code] ?? '요청이 실패했습니다.')
}

/**
 * 서버가 내는 detail 코드 → 사용자 문구. **코드가 곧 계약이다.**
 * 출처: server/accounts.py(signup/login)·server/main.py(라우트 가드).
 */
const MESSAGES: Record<string, string> = {
  // 계정(§10.0)
  pending_approval: '관리자 승인 대기 중인 계정입니다.',
  invalid_credentials: '아이디 또는 비밀번호가 올바르지 않습니다.',
  disabled: '사용이 중지된 계정입니다. 관리자에게 문의해 주세요.',
  invalid_username: '아이디 형식이 올바르지 않습니다.',
  exists: '이미 사용 중인 아이디입니다.',
  weak_password: '비밀번호는 8자 이상이어야 합니다.',
  trivial_password: '너무 쉬운 비밀번호입니다. 다른 값을 써 주세요.',
  // 인가·입장
  unauthenticated: '로그인이 필요합니다.',
  forbidden: '권한이 없습니다.',
  // §10.0 방 개설 권한은 승인과 **다른 축**이다 — 로그인은 되는데 개설만
  // 막힌 상태이므로, "권한이 없습니다" 같은 뭉뚱그린 문구는 사용자를
  // 로그인 문제로 오해하게 만든다. 무엇이 막혔고 누구에게 물어야 하는지 쓴다.
  room_create_forbidden:
    '방 개설 권한이 없습니다. 관리자에게 권한을 요청해 주세요.',
  recording_unavailable: '이 서버는 기록을 저장하지 않도록 구성되어 있습니다.',
  // §10.2 존재 미누출 — 없는 방·틀린 패스코드·비공개 방이 모두 이 코드다.
  invalid_room_or_passcode: '입장할 수 없습니다. 코드를 확인해 주세요.',
  too_many_attempts: '시도가 너무 잦습니다. 잠시 후 다시 시도해 주세요.',
  rate_limited: '요청이 너무 잦습니다. 잠시 후 다시 시도해 주세요.',
  not_found: '대상을 찾을 수 없습니다.',
  // §S25 원문 직역 온디맨드
  expired: '원문이 만료되어 번역할 수 없습니다.',
  unavailable: '지금은 번역할 수 없습니다. 잠시 후 다시 시도해 주세요.',
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'same-origin', // 세션 쿠키(§10.0 — trans_session)
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  })
  const text = await res.text()
  let body: unknown = {}
  try {
    body = text ? JSON.parse(text) : {}
  } catch {
    body = {}
  }
  if (!res.ok) throw errorFrom(res.status, body)
  return body as T
}

/* ── 서버 기능 스위치 ───────────────────────────────────────────────────── */

/**
 * 서버가 켜 둔 기능. 화면은 이걸 보고 **없는 기능의 버튼을 그리지 않는다**.
 * 인증 불요 — 익명 패스코드 뷰어도 같은 판단이 필요하다.
 */
export interface ServerConfig {
  /** §8.5 회의 중 채팅 */
  chat: boolean
  /** §7.2 코치 피드 */
  coach: boolean
  /**
   * 어시스트 채팅(DEF-071). **방별 설정이 아니라 전역**이다 — `room.coach`
   * 에 매달면 기본값이 꺼짐이라 기존 방 전부에서 죽은 채 화면만 살아 있다
   * (`CreateRoomInput.coach` 주석의 그 사고).
   */
  assist: boolean
  /**
   * 디스크 기록이 살아 있는가(서버 `TRANS_RECORD_DIR` 설정 여부). 방별
   * `record` 스위치와 **다른 축**이다 — 켜도 이게 false 면 메모리 링
   * 잔여분만 남는다. 개설 화면은 이때 "디스크 기록 꺼짐(서버 설정)" 힌트를
   * 그려야 한다(조용한 유실 금지).
   */
  record_persisted: boolean
  /**
   * 서버가 실제로 받아 주는 언어 목록(정본 `server/languages.py`
   * `as_payload()`). **숨김 원어(`TRANS_HIDDEN_SOURCE_LANGS`)가 이미
   * 빠져 있다** — 개설 화면은 생성 미러가 아니라 이 목록으로 원어
   * 선택지를 그린다(필터를 한 곳에만 둔다).
   *
   * 구버전 서버는 이 필드를 안 보낸다 → `undefined`. 그때는 생성
   * 미러로 되돌아간다(목록이 비어 아무 언어도 못 고르는 것보다 낫다).
   */
  languages?: readonly ServerLanguage[]
}

/** `/api/config` 의 언어 한 줄 — `Language` 의 **부분집합**이다(script 없음). */
export interface ServerLanguage {
  code: string
  ko: string
  en: string
  tier: string
  source: boolean
  target: boolean
  note: string
}

export function fetchConfig(): Promise<ServerConfig> {
  return request('/config')
}

/* ── §10.0 계정 ─────────────────────────────────────────────────────────── */

/** 서버 `/api/me` 응답(`AccountStore.public()`). */
export interface Account {
  username: string
  status: 'pending' | 'approved'
  is_admin: boolean
}

export function signup(
  username: string,
  password: string,
): Promise<{ ok: true; status: 'pending' | 'approved' }> {
  return request('/signup', { method: 'POST', body: JSON.stringify({ username, password }) })
}

/**
 * 로그인 — 성공 시 서버가 **`trans_session`** 쿠키(HttpOnly)를 심는다.
 *
 * 주의: `trans_sid` 는 인증 쿠키가 **아니다**. 그것은 §10.2 입장 백오프의
 * 세션지문(방+지문으로 대입을 잠그는 축)이며, 로그인 여부와 무관하게 발급된다.
 * 둘을 혼동해 trans_sid 로 로그인 상태를 판정하면, 코드 입장만 시도한 익명
 * 방문자가 로그인한 것처럼 보인다.
 */
export async function login(username: string, password: string): Promise<Account> {
  await request<{ ok: true; username: string; is_admin: boolean }>('/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  })
  // 계정이 바뀌었다 — 이전 계정의 권한 토큰이 살아남으면 안 된다(§5.2).
  clearAccountToken()
  // 로그인 응답은 요약형이라 정본 계정 상태를 다시 읽는다(단일 출처).
  return me()
}

export function me(): Promise<Account> {
  return request('/me')
}

export function logout(): Promise<{ ok: true }> {
  // 세션과 함께 파생 자격(권한 토큰 캐시)도 버린다 — 로그아웃했는데 방
  // 개설이 계속 되는 상태(≤TTL 잔여)를 이 탭에서는 만들지 않는다.
  clearAccountToken()
  return request('/logout', { method: 'POST' })
}

/* ── 방 ─────────────────────────────────────────────────────────────────── */

export interface CreateRoomInput {
  src: string
  dst: string
  public_entry: boolean
  record: boolean
  retention_days: 7 | 30 | 90
  /**
   * §7.2 방별 코치 opt-in.
   *
   * 이 필드가 없어서 **코치는 어떤 경로로도 켤 수 없었다**: 서버 기본이
   * `coach=False` 이고 게이트가 `settings.coach and room.coach` 인데,
   * 개설 요청이 이 값을 아예 안 보내니 모든 UI 방이 `coach=False` 로
   * 만들어졌다. 그러면서 호스트 화면은 코치 패널을 켜진 것처럼 그렸다 —
   * 기능이 죽어 있는데 화면은 살아 있다고 말하는 상태였다.
   */
  coach: boolean
  /**
   * 코치를 **참가자에게도** 보일지(§7.2.2). 기본 공개.
   * 개인 취향이 아니라 방의 성격이다 — 호스트가 정하고 뷰어는 못 바꾼다.
   * ([제안]·[불일치]는 호스트에게 주는 조언이라, 상대측과 화면을 같이
   *  보는 자리에서는 끌 수 있어야 한다.)
   */
  coach_public: boolean
  /**
   * 어시스트 답을 참가자에게도 보일지(D1). 기본 off — 코치와 달리
   * **사람이 쓴 질문**이라 기본 전체 공개는 되돌릴 수 없는 노출이다.
   */
  assist_public?: boolean
  /**
   * 양방향 통역(§ bidi). 켜면 src/dst 는 방향이 아니라 언어 **쌍**을 뜻하고,
   * 쌍 안의 어느 쪽으로 말해도 반대쪽으로 통역한다(예: ko↔vi, fr↔ko). 서버는
   * **비대칭** 규칙으로 허용한다 — dst 가 유효 타깃(ko·vi)이고 src 가 역번역
   * 목적지로 열린 코드(REVERSE_TARGET_CODES: ko·vi·fr)일 때. fr→ko 방이면
   * 한국어 발화만 fr 로, 나머지는 ko 로. 아니면 400 bidirectional_pair_unsupported.
   * 기본 false(단방향).
   */
  bidirectional?: boolean
  /**
   * 이전 회의 이어하기(S23) — 이전 회의 코드. 비우면 이어하기 아님.
   */
  continue_from?: string
  /** S28 — 회의 이름(opt-in). 서버가 기본값을 지어내지 않으므로 항상 보낸다. */
  name: string
  /** S28 — 회의 주제(opt-in). 서버가 기본값을 지어내지 않으므로 항상 보낸다. */
  topic: string
  /** S28 — 호스트의 음성 녹음 opt-out/opt-in. 화면은 항상 구체적인 값을 보낸다. */
  record_audio: boolean
}

export interface CreatedRoom {
  /** 불투명 방 ID — URL 경로에 실리는 축(§10.0) */
  room: string
  /** 6자리 입장 패스코드 — **개설 응답이 이걸 담는 유일한 지점**이다 */
  passcode: string
  host_key: string
  view_key: string
  src: string
  dst: string
  /** 양방향 통역 여부(§ bidi). 구 서버는 안 준다 → undefined = 단방향 */
  bidirectional?: boolean
  record: boolean
  /**
   * 기록이 **디스크까지** 가는가 = `record && 서버 record_dir 설정됨`.
   * 구 서버는 안 준다 → undefined. false 면 메모리 링 잔여분만 남는다.
   */
  record_persisted?: boolean
  public_entry: boolean
  retention_days: number
  /** §7.2 방별 코치 opt-in — 서버가 항상 준다(구 서버는 필드 자체가 없다) */
  coach?: boolean
  /** 코치를 참가자에게도 보일지(§7.2.2) — 서버가 항상 준다 */
  coach_public?: boolean
  /** 어시스트를 참가자에게도 보일지(D1) — 서버가 항상 준다 */
  assist_public?: boolean
  /** §5.2 host 토큰 — 이 방의 오디오 업링크·제어 자격(정본) */
  token?: string
  /** 토큰 TTL(초) — 만료 계산의 근거. 구 서버는 안 준다 */
  token_ttl_s?: number
  /** 이어하기(S23) 원본 회의 코드. 새 서버는 항상 준다 — 구 서버는 안 준다 */
  continued_from?: string
  /** 이어하기로 이식된 항목 수(S23). 새 서버는 항상 준다 — 구 서버는 안 준다 */
  seeded?: number
  /** S28 회의 이름. 서버가 항상 준다(빈 문자열 포함) — 구 서버는 안 준다 */
  name?: string
  /** S28 회의 주제. 서버가 항상 준다(빈 문자열 포함) — 구 서버는 안 준다 */
  topic?: string
}

export interface MeetingSummary {
  id: number
  room: string
  owner: string
  src: string
  dst: string
  started_at: number
  ended_at: number
  end_reason: string
  retention_days: number
  captions: number
  chats: number
  finalized_at: number
  /** S28 회의 이름. 구 회의는 없을 수 있다 */
  name?: string
  /** S28 회의 주제. 구 회의는 없을 수 있다 */
  topic?: string
}

export async function listMyMeetings(): Promise<{ meetings: MeetingSummary[] }> {
  return request('/meetings/mine', {
    method: 'GET',
    headers: await accountAuthHeaders(),
  })
}

/**
 * 방 개설 — **stream 서비스의 계정 단위 라우트**다(분할표: /api/rooms).
 *
 * 분리 형상에서 stream 에는 세션이 없으므로, auth 가 로그인 세션으로 발급한
 * **cap:create 권한 토큰**(Bearer)이 개설 권한을 운반한다(§5.2 경로 2 —
 * 서버간 호출 0, 브라우저가 버스다). 단일 프로세스에서는 세션 쿠키만으로도
 * 동작하므로 토큰 발급 실패는 요청을 막지 않는다(서버 판정이 정본).
 */
export async function createRoom(input: CreateRoomInput): Promise<CreatedRoom> {
  return request('/rooms', {
    method: 'POST',
    headers: await accountAuthHeaders(),
    body: JSON.stringify(input),
  })
}

export interface JoinResult {
  ok: true
  room: string
  /** 구 view_key — 서버가 더는 싣지 않는다(자격은 `token`). 구 서버 호환. */
  view_key?: string
  src: string
  dst: string
  /** 양방향 통역 여부(§ bidi). 구 서버는 안 준다 → undefined = 단방향 */
  bidirectional?: boolean
  /** S28 회의 이름. 구 서버는 안 준다 */
  name?: string
  /** S28 회의 주제. 구 서버는 안 준다 */
  topic?: string
  anonymous: boolean
  /** §5.2 viewer 토큰 — 패스코드 지식을 서명된 단기 자격으로 교환한 것 */
  token?: string
  /** 토큰 TTL(초) — 만료 계산의 근거. 구 서버는 안 준다 */
  token_ttl_s?: number
}

/**
 * 6자리 패스코드로 입장(§10.0 — 두 번째 요소).
 *
 * 보내는 것은 **패스코드 하나**다. 방 ID 는 서버가 응답으로 알려준다 —
 * 그래야 패스코드가 URL 경로에 실릴 일이 없다(프록시 로그·브라우저 히스토리
 * 누출 차단).
 *
 * `authenticated` 면 계정 토큰(Bearer)을 함께 싣는다 — **인가가 아니라
 * 신원 축**이다(§5.5): 분리 형상에서 join 은 stream 에 닿고 stream 은
 * 세션을 볼 수 없으므로, 로그인 뷰어의 계정명(감사 표시·채팅 이름 점유
 * 주체)은 토큰 sub 로만 도달한다. 익명 입장은 헤더 없이 그대로다 —
 * 익명 경로에 auth 왕복을 얹지 않는다(auth 다운 무영향 유지).
 */
export async function joinByPasscode(
  passcode: string,
  opts: { authenticated?: boolean } = {},
): Promise<JoinResult> {
  return request('/rooms/join', {
    method: 'POST',
    headers: opts.authenticated ? await accountAuthHeaders() : {},
    body: JSON.stringify({ passcode }),
  })
}

/** §10.2 입장 안내(공개) — 비밀 없음. 직통 링크 부트스트랩이 쓴다. */
export interface RoomEntryInfo {
  room: string
  state: string
  public_entry: boolean
  passcode_required: boolean
  /** S28 회의 이름 — 공개 입장이 열린 방만 채워진다(그 외는 빈 문자열) */
  name?: string
  /** S28 회의 주제 — 공개 입장이 열린 방만 채워진다(그 외는 빈 문자열) */
  topic?: string
  /** S36 — 방의 원언어. 공개 입장이 열린 방만 채워진다(그 외는 빈 문자열).
   *  구 서버는 이 필드를 안 준다(undefined) — 옵셔널로 다뤄야 한다. */
  src?: string
  /** S36 — 방의 타깃 언어. src 와 같은 노출 규칙. */
  dst?: string
  /** S36 — 양방향 여부. src 와 같은 노출 규칙. */
  bidirectional?: boolean
}

export function roomEntry(room: string): Promise<RoomEntryInfo> {
  return request(`/rooms/${encodeURIComponent(room)}/entry`)
}

/**
 * 직통 링크 비밀(`/v/{room}#k=…`) → viewer 토큰 교환 응답. join 과 같은
 * 모양이되 옛 키·패스코드는 없다 — 갱신은 같은 비밀로 같은 경로다.
 */
export interface LinkExchangeResult {
  ok: true
  room: string
  src: string
  dst: string
  bidirectional?: boolean
  name?: string
  topic?: string
  anonymous: boolean
  token: string
  token_ttl_s?: number
}

/**
 * 직통 링크 교환(§10.2, sec SEC-03 후속) — stream 소유 경로라 auth 가
 * 죽어도 된다. 실패는 전부 uniform 403(없는 방·닫힌 방·비공개 방·틀린
 * 비밀이 같다) — 화면은 한 문구로 묶는다.
 */
export function exchangeLinkKey(room: string, key: string): Promise<LinkExchangeResult> {
  return request(`/rooms/${encodeURIComponent(room)}/link`, {
    method: 'POST',
    body: JSON.stringify({ key }),
  })
}

/**
 * 방 개설 이력 1행(S28) — `room_creations` 테이블의 원시 행 모양.
 * `GET /api/rooms/created`·`GET /api/admin/rooms/created` 가 이 모양으로 준다.
 */
export interface RoomCreationRow {
  room: string
  owner: string
  name: string
  topic: string
  src: string
  dst: string
  bidirectional: boolean
  record: boolean
  public_entry: boolean
  dump_audio: boolean
  retention_days: number
  created_at: number
  closed_at: number
  end_reason: string
  captions: number
  chats: number
}

export interface RoomCreationList {
  items: RoomCreationRow[]
  total: number
  limit: number
  offset: number
}

/** 내가 개설한 회의 이력(S28) — 종료 여부와 무관하게 전부. */
export async function listCreatedRooms(
  limit?: number,
  offset?: number,
): Promise<RoomCreationList> {
  const params = new URLSearchParams()
  if (limit !== undefined) params.set('limit', String(limit))
  if (offset !== undefined) params.set('offset', String(offset))
  const qs = params.toString()
  return request(`/rooms/created${qs ? `?${qs}` : ''}`, {
    headers: await accountAuthHeaders(),
  })
}

/** 전체 개설 이력(관리자, S28) — `owner` 를 주면 그 계정만. */
export async function adminListCreatedRooms(
  limit?: number,
  offset?: number,
  owner?: string,
): Promise<RoomCreationList> {
  const params = new URLSearchParams()
  if (limit !== undefined) params.set('limit', String(limit))
  if (offset !== undefined) params.set('offset', String(offset))
  if (owner) params.set('owner', owner)
  const qs = params.toString()
  return request(`/admin/rooms/created${qs ? `?${qs}` : ''}`, {
    headers: await accountAuthHeaders(),
  })
}

/**
 * 방의 **서버 진실**(호스트 전용) — 새로고침 후 복원의 단일 출처.
 *
 * 호스트 콘솔은 새로고침되면 메모리 상태를 잃는다. 방에 속한 값(기록 여부·
 * 보존 기간·통역 방향)을 클라이언트 기본값으로 다시 그리면 서버와 어긋난
 * 화면이 만들어진다 — 실제로 기록 ON 으로 만든 방이 설정 서랍에 OFF 로
 * 보였다. 방의 상태는 방이 갖고 있고, 화면은 그것을 읽어 그린다.
 */
export interface RoomSettings {
  room: string
  /** S28 회의 이름. 구 서버는 안 준다 */
  name?: string
  /** S28 회의 주제. 구 서버는 안 준다 */
  topic?: string
  src: string
  dst: string
  /** 양방향 통역 여부(§ bidi). 구 서버는 안 준다 → undefined = 단방향 */
  bidirectional?: boolean
  record: boolean
  retention_days: number
  public_entry: boolean
  state: string
  /** §11.3 — 이제 회의 도중에도 바꿀 수 있다(항상 false, 구 서버 호환용) */
  record_locked: boolean
  /** 서버가 기록 가능한 상태인가(무저장 배포면 false) */
  record_available?: boolean
  /** §7.2 이 방이 코치를 켰는가(개설 시 opt-in) */
  coach?: boolean
  /** 서버 전역 코치 스위치. false 면 방이 켰어도 코치는 안 돈다 */
  coach_available?: boolean
  /** 어시스트 답을 참가자에게도 보일지(D1) — 서버가 항상 준다 */
  assist_public?: boolean
}

/**
 * §11.3 회의 **도중** 기록 켜기/끄기 — 호스트 전용.
 *
 * 끄기는 **삭제가 아니다**: 이미 저장된 자막은 보존 기간까지 남는다.
 * 화면도 그 사실을 말해야 한다("이미 저장된 자막은 보존 기간까지 유지됩니다").
 */
export function setRecord(
  room: string,
  hostKey: string,
  record: boolean,
  /**
   * 보존 기간(일) — 화면의 7/30/90 선택. **서버가 클램프한다**(§11).
   * 응답의 `retention_days` 가 정본이므로 화면은 그 값을 신뢰한다.
   *
   * 예전에는 이 인자가 아예 없어서 선택이 서버에 도달하지 못했다 —
   * 사용자가 90일을 골라도 조용히 기본값이었다. 생략하면 서버는 방의
   * 현재 값을 유지한다(토글만 하는 호출).
   */
  retentionDays?: number,
): Promise<{ ok: true; record: boolean; retention_days: number }> {
  return request(`/rooms/${encodeURIComponent(room)}/record`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${hostKey}` },
    body: JSON.stringify(
      retentionDays === undefined
        ? { record }
        : { record, retention_days: retentionDays },
    ),
  })
}

export function fetchRoomSettings(room: string, hostKey: string): Promise<RoomSettings> {
  return request(`/rooms/${encodeURIComponent(room)}/settings`, {
    headers: { Authorization: `Bearer ${hostKey}` },
  })
}

/* ── §8.3 히스토리 ──────────────────────────────────────────────────────── */

/**
 * 히스토리 페이지네이션 — **Bearer 키가 필수**다(§10.1).
 *
 * 서버는 view_key/host_key 범위를 이 헤더로 판정한다. 헤더 없이 부르면 403 이라
 * 무한 스크롤이 조용히 죽는다(빈 페이지 = "더 없음" 으로 보인다).
 */
export async function fetchHistory(
  room: string,
  key: string,
  beforeSeq: number,
  limit: number = HISTORY_PAGE,
): Promise<Envelope[]> {
  const res = await request<{ events: Envelope[] }>(
    `/rooms/${encodeURIComponent(room)}/history?before_seq=${beforeSeq}&limit=${limit}`,
    { headers: { Authorization: `Bearer ${key}` } },
  )
  return res.events
}

/* ── S25 원문 직역 온디맨드 ─────────────────────────────────────────────── */

/** 서버 `POST /api/rooms/{code}/xlate` 응답 — src/dst 는 서버가 정한다. */
export interface XlateResult {
  ok: true
  utt: string
  src_lang: string
  dst_lang: string
  text: string
  unverified: true
}

/**
 * "확인 필요" 카드의 원문을 그 자리에서 번역해 본다 — **utt id 만** 보낸다.
 *
 * 서버가 자기 링버퍼에서 utt 로 원문을 찾아 번역한다(open-proxy 방지 —
 * 텍스트를 클라이언트가 실어 보내지 않는다). 히스토리와 같은 Bearer 규율.
 */
export function translateSource(room: string, key: string, utt: string): Promise<XlateResult> {
  return request(`/rooms/${encodeURIComponent(room)}/xlate`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${key}` },
    body: JSON.stringify({ utt }),
  })
}

/* ── §8.4 삭제 (host_key Bearer) ────────────────────────────────────────── */

export function deleteCaption(
  room: string,
  hostKey: string,
  target: { seq?: number; utt?: string },
): Promise<{ ok: true; removed: number }> {
  return request(`/rooms/${encodeURIComponent(room)}/delete`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${hostKey}` },
    // 서버 DeleteBody 는 target_seq:int / target_utt:str 다 — null 을 보내면
    // 검증 오류(422)가 난다. 미지정은 기본값(0 / "")으로 표현한다.
    body: JSON.stringify({
      target_seq: target.seq ?? 0,
      target_utt: target.utt ?? '',
    }),
  })
}


/** §8.5 채팅 삭제 — 호스트만. 자막 삭제(§8.4)와 같은 규율(tombstone). */
export function deleteChat(
  room: string,
  hostKey: string,
  targetSeq: number,
): Promise<{ ok: true; removed: number }> {
  return request(`/rooms/${encodeURIComponent(room)}/chat/delete`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${hostKey}` },
    body: JSON.stringify({ target_seq: targetSeq, target_utt: '' }),
  })
}

/* ── on-demand 요약/의도 해석(§8.2 summary) ─────────────────────────────── */

/** 결과는 REST 응답이 아니라 `summary` 이벤트로 소켓에 도착한다.
 *
 *  `kind` 는 요약(summary)과 의도 해석(intent)을 가른다. 예전엔 두 버튼이
 *  **같은 본문 없는 요청**을 보내 서버가 구분할 수 없었고, 결과가 늘 같았다. */
export function requestAnalyze(
  room: string,
  hostKey: string,
  kind: 'summary' | 'intent' = 'summary',
): Promise<{ ok: true }> {
  return request(`/rooms/${encodeURIComponent(room)}/analyze`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${hostKey}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ kind }),
  })
}

/* ── 어시스트 질의(DEF-071) ──────────────────────────────────────────────── */

/**
 * 회의 기록에 대한 질의 1건. 결과는 REST 응답이 아니라 `assist` 이벤트로
 * 소켓에 도착한다(요약과 같은 축 — 다른 탭·재접속도 같은 답을 본다).
 *
 * 거절 사유는 status 로 온다: 429 `rate_limited`/`assist_busy`,
 * 503 `lag_blocked`. 호출측이 그대로 화면에 말해야 한다(조용한 실패 금지).
 */
export function requestAssist(
  room: string,
  hostKey: string,
  q: string,
): Promise<{ ok: true }> {
  return request(`/rooms/${encodeURIComponent(room)}/assist`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${hostKey}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ q }),
  })
}

/**
 * §8.2 방향 전환 — 호스트 전용.
 *
 * `bidirectional` 을 켜면 src/dst 는 방향이 아니라 언어 **쌍**을 뜻하고,
 * 쌍 안의 어느 쪽으로 말해도 반대쪽으로 통역한다(§ bidi). 서버는 두 언어가
 * 모두 유효 타깃인 쌍(오늘 {ko, vi})에서만 허용한다 — 아니면 400.
 */
export function setDirection(
  room: string,
  hostKey: string,
  src: string,
  dst: string,
  bidirectional = false,
): Promise<{ ok: true; src: string; dst: string; bidirectional?: boolean }> {
  return request(`/rooms/${encodeURIComponent(room)}/direction`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${hostKey}` },
    body: JSON.stringify({ src, dst, bidirectional }),
  })
}

/** §9 명시 종료 — 호스트 전용. */
export function closeRoom(room: string, hostKey: string): Promise<{ ok: true }> {
  return request(`/rooms/${encodeURIComponent(room)}/close`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${hostKey}` },
  })
}
